import { Checkbox } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import * as Yup from 'yup';

import Button from '../../components/Button';
import Input from '../../components/Input';
import { showError, showToast } from '../../constants/toasts';
import { getFromStorage, removeFromStorage, setToStorage, STORAGE_KEYS } from '../../helpers';
import { useAppDispatch } from '../../hooks/store';
import { setCredentials } from '../../reducers/authSlice';
import { usePostLoginMutation } from '../../service/auth';
import { CommonBody } from '../../types/General';
import { generateEncryptedKeyBody } from '../../utils/crypto';

import AuthLayout from './AuthLayout';

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loginMutation] = usePostLoginMutation();

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      remember: false,
    },
    validationSchema: Yup.object({
      remember: Yup.boolean(),
      email: Yup.string()
        .required('Email is required')
        .matches(
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
          'Please enter a valid email address'
        ),
      password: Yup.string().required('Password is required'),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);
      const fcmToken = getFromStorage(STORAGE_KEYS.fcmToken);

      const body = {
        email: values.email,
        password: values.password,
        deviceToken: fcmToken || '123',
        deviceType: 'WEB',
      };

      try {
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await loginMutation(encryptedBody).unwrap();
        if (response?.statusCode === 200) {
          showToast('Logged In Successfully.');
          setToStorage(STORAGE_KEYS.token, response?.data?.accessToken || '');
          dispatch(
            setCredentials({
              user: response?.data || null,
              token: response?.data?.accessToken || '',
            })
          );

          if (formik.values.remember) {
            setToStorage(STORAGE_KEYS.credentials, JSON.stringify(body));
          } else {
            removeFromStorage(STORAGE_KEYS.credentials);
          }

          navigate('/dashboard');
        }
      } catch (error: any) {
        showError(error?.data?.message || '');
        console.error(error);
      }
    },
  });

  useEffect(() => {
    const data = getFromStorage(STORAGE_KEYS.credentials);
    if (data) {
      const rememberData = JSON.parse(`${data}`);
      formik.setFieldValue('password', rememberData?.password);
      formik.setFieldValue('email', rememberData?.email);
      formik.setFieldValue('remember', true);
    }
    // eslint-disable-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthLayout>
      <div className="authBox">
        <h2>Sign In</h2>
        <p>Welcome to Car Marketplace admin panel</p>
        <form onSubmit={formik.handleSubmit}>
          <Input
            placeholder="Email"
            name="email"
            id="email"
            inputProps={{ maxLength: 80 }}
            value={formik.values.email}
            onChange={(val) => {
              if (val.target.value === ' ' || val.target.value === '.') {
              } else {
                formik.handleChange(val);
              }
            }}
            onBlur={formik.handleBlur}
            helperText={formik.touched.email && formik.errors.email}
          />
          <Input
            placeholder="Password"
            password
            id="password"
            name="password"
            value={formik.values.password}
            onChange={(val) => {
              if (val.target.value === ' ' || val.target.value === '.') {
              } else {
                formik.handleChange(val);
              }
            }}
            onBlur={formik.handleBlur}
            helperText={formik.touched.password && formik.errors.password}
          />
          <div className="form_control d_flex">
            <div className="remember_box">
              <FormControlLabel
                control={<Checkbox />}
                checked={formik.values.remember}
                name="remember"
                onChange={formik.handleChange}
                label="Remember me"
              />
            </div>
            <a
              href="javacript:void(0)"
              className="anchor_link"
              onClick={() => navigate('/forgot-password')}
            >
              Forgot Password?
            </a>
          </div>

          <Button value="Sign In" type="submit" />
        </form>
      </div>
    </AuthLayout>
  );
};

export default Login;
